import { cloneDeep } from '@dynamic/ui';
import { proxy, useSnapshot } from 'valtio';
import { subscribeKey } from 'valtio/utils';

import {
  FeedResponse,
  HistoryAllItem,
  historyService,
  Item,
  ResourceType,
  WatchHistoryItemResponse,
} from '#/client';
import devtoolsApi from '#/dev/devtools';
import { ignoreError } from '#/service/utils';
import { authStore } from '#/store/auth';

interface HistoryStore {
  history: { [id: string]: HistoryAllItem };
}

const defaultState: HistoryStore = {
  history: {},
};

/** Store for watch history */
export const historyStore = proxy<HistoryStore>(defaultState);

/** Store actions for watch history */
export const historyActions = {
  // load the full history and cache the results. used during app startup.
  async loadAll() {
    try {
      // fetch all history
      const { data } = await historyService.getAll();
      // update cache
      (data.data ?? []).forEach((historyItem) => {
        // clone the history item to prevent later updates mutate the original
        historyStore.history[historyItem.objectId] = cloneDeep(historyItem);
      });
    } catch (err) {
      console.warn('Watch history: loadAll failed', err);
    }
  },

  // get a page of the history feed, and update the cache too. used for watch history page.
  async getPage(page: number): Promise<FeedResponse> {
    return historyService.getPage(page).then(({ data: res }) => {
      // update cache
      (res.data ?? []).forEach((item) => {
        historyStore.history[item.id] = {
          objectId: item.id,
          objectType: item.type,
          watched: item.video.watched,
        };
      });

      return res;
    });
  },

  // get history for a single item, and update the cache too. used for details page.
  async getOne(
    resourceType?: ResourceType,
    resourceId?: string
  ): Promise<WatchHistoryItemResponse> {
    return historyService
      .getOne(resourceType, resourceId)
      .then(({ data: res }) => {
        const { data: item } = res;
        // update cache
        historyStore.history[item.id] = {
          objectId: item.id,
          objectType: item.type,
          watched: item.video.watched,
        };

        return res;
      });
  },

  // updates the cached history entry for a single item, as we are playing it.
  update(item: Item, currentTime: number, duration: number) {
    const cachedWatched = historyStore.history[item.id]?.watched;

    if (!cachedWatched) {
      return;
    }

    cachedWatched.progress = {
      finished: currentTime >= (item.video.markers?.fullyWatched ?? duration),
      percentage: Math.floor((currentTime / duration) * 100),
      seconds: currentTime,
    };
  },

  // empties the cache. used for testing.
  reset() {
    historyStore.history = {};
  },

  // clone the cached `watched` object into all items. this is needed for history related features on browse pages.
  cloneWithWatchedObjects(items: Item[]) {
    if (!items) {
      return [];
    }

    return (
      items.map((item) => {
        if (!item.video) {
          return item;
        }
        const cachedWatched = historyStore.history[item.id]?.watched;

        return {
          ...item,
          video: {
            ...item.video,
            watched: cachedWatched ?? item.video.watched,
          },
        };
      }) ?? []
    );
  },
};

// watch for changes in the logged in state, and load/reset history accordingly.
subscribeKey(authStore, 'isLoggedIn', (isLoggedIn) => {
  if (isLoggedIn) {
    historyActions.loadAll().catch(ignoreError);
  } else {
    historyActions.reset();
  }
});

export const useHistory = () => useSnapshot(historyStore) as HistoryStore;

devtoolsApi.setStore('history', historyStore, historyActions);
